/**
 * CandleScript - JSON Schema for saving/loading indicators
 */

/**
 * Indicator configuration schema
 * @typedef {Object} IndicatorConfig
 * @property {string} id - Unique identifier
 * @property {string} name - Display name
 * @property {string} description - Description
 * @property {string} code - Indicator source code
 * @property {string} side - 'onchart' or 'offchart'
 * @property {string} category - Category (trend, oscillator, volatility, custom)
 * @property {Array<OptionDef>} options - Input options definitions
 * @property {Object} plotConfig - Plot configuration
 * @property {Object} currentValues - Current option values
 * @property {number} createdAt - Creation timestamp
 * @property {number} updatedAt - Last update timestamp
 * @property {string} version - Schema version
 */

/**
 * @typedef {Object} OptionDef
 * @property {string} key - Option key name
 * @property {string} label - Display label
 * @property {string} type - 'number' | 'select' | 'boolean' | 'color'
 * @property {*} default - Default value
 * @property {number} [min] - Min value (for number)
 * @property {number} [max] - Max value (for number)
 * @property {number} [step] - Step value (for number)
 * @property {Array<string>} [choices] - Choices (for select)
 */

export const SCHEMA_VERSION = '1.0.0';

/**
 * Create a new indicator config
 */
export function createIndicatorConfig(overrides = {}) {
    return {
        id: overrides.id || generateId(),
        name: overrides.name || 'Custom Indicator',
        description: overrides.description || '',
        code: overrides.code || '',
        side: overrides.side || 'offchart',
        category: overrides.category || 'custom',
        options: overrides.options || [],
        plotConfig: overrides.plotConfig || {
            outputs: [{ key: 'value', label: 'Output', color: '#2962ff', width: 1.5, style: 'line' }],
        },
        currentValues: overrides.currentValues || {},
        createdAt: overrides.createdAt || Date.now(),
        updatedAt: Date.now(),
        version: SCHEMA_VERSION,
    };
}

/**
 * Validate an indicator config
 */
export function validateConfig(config) {
    const errors = [];

    if (!config) {
        return { valid: false, errors: ['Config is null or undefined'] };
    }

    if (!config.name || typeof config.name !== 'string') {
        errors.push('Name is required and must be a string');
    }

    if (!config.code || typeof config.code !== 'string') {
        errors.push('Code is required and must be a string');
    }

    if (!['onchart', 'offchart'].includes(config.side)) {
        errors.push('Side must be "onchart" or "offchart"');
    }

    if (config.options && !Array.isArray(config.options)) {
        errors.push('Options must be an array');
    }

    if (config.options) {
        for (let i = 0; i < config.options.length; i++) {
            const opt = config.options[i];
            if (!opt.key) errors.push(`Option ${i}: key is required`);
            if (!opt.type) errors.push(`Option ${i}: type is required`);
            if (!['number', 'select', 'boolean', 'color'].includes(opt.type)) {
                errors.push(`Option ${i}: type must be number, select, boolean, or color`);
            }
        }
    }

    return { valid: errors.length === 0, errors };
}

/**
 * Serialize config to JSON string
 */
export function serializeConfig(config) {
    return JSON.stringify(config, null, 2);
}

/**
 * Deserialize config from JSON string
 */
export function deserializeConfig(json) {
    try {
        const config = JSON.parse(json);
        const validation = validateConfig(config);
        if (!validation.valid) {
            return { config: null, errors: validation.errors };
        }
        return { config, errors: [] };
    } catch (e) {
        return { config: null, errors: ['Invalid JSON: ' + e.message] };
    }
}

/**
 * Generate a unique ID
 */
function generateId() {
    return 'cs_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 6);
}

/**
 * Get default values from options array
 */
export function getDefaultValues(options) {
    const values = {};
    if (!options) return values;
    for (const opt of options) {
        values[opt.key] = opt.default !== undefined ? opt.default : null;
    }
    return values;
}

export default {
    SCHEMA_VERSION,
    createIndicatorConfig,
    validateConfig,
    serializeConfig,
    deserializeConfig,
    getDefaultValues,
};
